import {
  acceptedNoomClinicalOffer,
  getSelectedMedPlanType,
  isTelex283Active,
  isTelex293Active,
  isTelex294aActive,
  isTelex294bActive,
} from "src/utils/userSegment/features";
import { PaymentEnrollmentFormState } from "src/utils/redux/slices/paymentEnrollmentForm";
import {
  getNoomSessionStorage,
  setNoomSessionStorage,
} from "src/utils/noomSessionStorage";
import {
  SessionStorageKey,
  MedProductType,
  BRANDED_MED_ESTIMATED_VALUE,
  COMPOUNDED_MED_ESTIMATED_VALUE,
  COMPOUNDED_MED_TELEX293_ESTIMATED_VALUE,
  ORALS_METFORMIN_MED_ESTIMATED_VALUE,
  HRT_PATCH_MED_ESTIMATED_VALUE,
  HRT_CREAM_MED_ESTIMATED_VALUE,
  LIRAGLUTIDE_MED_ESTIMATED_VALUE,
  MDS_MED_ESTIMATED_VALUE,
  MDS_MED_TELEX294A_ESTIMATED_VALUE,
  MDS_MED_TELEX294B_ESTIMATED_VALUE,
  COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER_MED_ESTIMATED_VALUE,
  COMPOUNDED_TIRZEPATIDE_MED_ESTIMATED_VALUE,
  COMPOUNDED_TIRZEPATIDE_TELEX283_MED_ESTIMATED_VALUE,
} from "src/utils/constants";
import {
  trackPurchaseIntent,
  PurchaseIntentDetails,
} from "src/utils/services/ConversionTracker";
import { appConfig } from "src/config";
import getStore from "src/utils/redux/store";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import { MED_SKU } from "@utils/medEligibility";
import { trackEvent } from "src/utils/api/tracker";

function getCompoundedMicrodoseEstimatedValue() {
  if (isTelex294aActive()) {
    return MDS_MED_TELEX294A_ESTIMATED_VALUE;
  }
  if (isTelex294bActive()) {
    return MDS_MED_TELEX294B_ESTIMATED_VALUE;
  }
  return MDS_MED_ESTIMATED_VALUE;
}

function getPurchaseIntentDetails(): PurchaseIntentDetails {
  const medPlanType = getSelectedMedPlanType();
  if (medPlanType === MED_SKU.COMPOUNDED) {
    return {
      estimatedValue: isTelex293Active()
        ? COMPOUNDED_MED_TELEX293_ESTIMATED_VALUE
        : COMPOUNDED_MED_ESTIMATED_VALUE,
      medProductType: MedProductType.GLP1,
      medPlanType,
    };
  }
  if (medPlanType === MED_SKU.HRT_CREAM) {
    return {
      estimatedValue: HRT_CREAM_MED_ESTIMATED_VALUE,
      medProductType: MedProductType.HRT,
      medPlanType,
    };
  }
  if (medPlanType === MED_SKU.HRT_PATCH) {
    return {
      estimatedValue: HRT_PATCH_MED_ESTIMATED_VALUE,
      medProductType: MedProductType.HRT,
      medPlanType,
    };
  }
  if (medPlanType === MED_SKU.ORALS_METFORMIN) {
    return {
      estimatedValue: ORALS_METFORMIN_MED_ESTIMATED_VALUE,
      medProductType: MedProductType.Orals,
      medPlanType,
    };
  }
  if (medPlanType === MED_SKU.GENERIC_LIRAGLUTIDE) {
    return {
      estimatedValue: LIRAGLUTIDE_MED_ESTIMATED_VALUE,
      medProductType: MedProductType.GLP1,
      medPlanType,
    };
  }
  if (medPlanType === MED_SKU.COMPOUNDED_MICRODOSE) {
    return {
      estimatedValue: getCompoundedMicrodoseEstimatedValue(),
      medProductType: MedProductType.GLP1,
      medPlanType,
    };
  }
  if (medPlanType === MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER) {
    return {
      estimatedValue:
        COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER_MED_ESTIMATED_VALUE,
      medProductType: MedProductType.GLP1,
      medPlanType,
    };
  }
  if (medPlanType === MED_SKU.COMPOUNDED_TIRZEPATIDE) {
    return {
      estimatedValue: isTelex283Active()
        ? COMPOUNDED_TIRZEPATIDE_TELEX283_MED_ESTIMATED_VALUE
        : COMPOUNDED_TIRZEPATIDE_MED_ESTIMATED_VALUE,
      medProductType: MedProductType.GLP1,
      medPlanType,
    };
  }
  return {
    estimatedValue: BRANDED_MED_ESTIMATED_VALUE,
    medProductType: MedProductType.GLP1,
    medPlanType,
  };
}

export function handleTermAgreementClick(
  checked: boolean,
  enrollmentInfo: PaymentEnrollmentFormState["enrollmentInfo"]
) {
  if (
    checked &&
    acceptedNoomClinicalOffer() &&
    !getNoomSessionStorage(SessionStorageKey.trackedCheckoutTermsAndConditions)
  ) {
    setNoomSessionStorage(
      SessionStorageKey.trackedCheckoutTermsAndConditions,
      true
    );

    const purchaseIntentDetails = getPurchaseIntentDetails();
    if (appConfig.MED_PIXEL_TRACKING_ENABLED && purchaseIntentDetails) {
      const coreState = getStore().getState();
      const surveyAnswers = getSurveyAnswers(coreState);
      const { recommendedPlan, checkoutOrders } = coreState;
      const { initialBillingCycle, recurringBillingCycle, prefetchedEltv } =
        checkoutOrders;

      trackPurchaseIntent(
        enrollmentInfo.email,
        recommendedPlan,
        surveyAnswers,
        purchaseIntentDetails,
        initialBillingCycle,
        recurringBillingCycle,
        prefetchedEltv?.eltvValue ?? null
      );

      trackEvent("EltvShadowComparison", {
        hardcodedEltv: purchaseIntentDetails.estimatedValue,
        mlEltv: prefetchedEltv?.eltvValue ?? null,
        mlEltvAvailable: prefetchedEltv != null,
        medPlanType: purchaseIntentDetails.medPlanType,
        planId: recommendedPlan?.braintree_id,
      });
    }
  }
}
